import { useEffect, useCallback } from "react";
import { useFormContext } from "react-hook-form";

import { Text } from "$components";
import useAuth from "$hooks/useAuth";
import useCompany from "$hooks/useCompany";
import SectorSelect from "$forms/SectorSelect";
import CompanySelect from "$forms/CompanySelect";
import { CompanyType } from "$types/CompanyType";
import CField, { CFieldInputProps } from "$forms/CField";
import { useStyles } from "../styles/CompanyForm.styles";
import CompanySiretSelect from "$forms/CompanySiretSelect";
import {
  emailRegexp,
  NAME_ERROR_MSG,
  nameRegexp,
  normalizeName,
} from "$utils/utils";
import { CompanyForm } from "..";
import { Company } from "$types";

type Props = {
  companyTypes: CompanyType[];
  defaultValues: Partial<Company>;
  formDefaultValues: CompanyForm;
  validateName: (name: string) => boolean;
  validateSiret: (siret: string) => boolean;
  fuzzySearchOnCompaniesName: (name: string) => void;
  isAutofillAlreadyUsed: boolean;
  isAutofillError: boolean;
  isCompanyFilledUsed: boolean;
  setIsCompanyFilledUsed: (value: boolean) => void;
  matchingCompanies: Company[];
  siretValue: string;
  handleAutoFillClicked: () => void;
};

export default function CompanyDetails({
  companyTypes,
  defaultValues,
  formDefaultValues,
  validateName,
  validateSiret,
  fuzzySearchOnCompaniesName,
  isAutofillAlreadyUsed,
  isAutofillError,
  isCompanyFilledUsed,
  setIsCompanyFilledUsed,
  matchingCompanies,
  siretValue,
  handleAutoFillClicked,
}: Props) {
  const { isAdmin } = useAuth();

  const isOnlySupplier =
    companyTypes.length === 1 && companyTypes[0] === CompanyType.SUPPLIER;
  const { isAgency, isCarrier } = useCompany();
  const {
    control,
    formState: { errors },
    setValue,
    trigger,
    watch,
  } = useFormContext();
  const classes = useStyles();

  const nameValue = watch("name");

  // Trigger initial search when component mounts with existing name
  useEffect(() => {
    if (nameValue && nameValue.length > 2) {
      fuzzySearchOnCompaniesName(nameValue);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Backup effect: trigger search when formDefaultValues.name is available
  useEffect(() => {
    if (
      formDefaultValues?.name &&
      formDefaultValues.name.length > 2 &&
      !nameValue
    ) {
      fuzzySearchOnCompaniesName(formDefaultValues.name);
    }
  }, [formDefaultValues?.name, nameValue, fuzzySearchOnCompaniesName]);

  const companySiretSelect = useCallback(
    (data: any) => {
      if (typeof data === "object" && data !== null) {
        if (data.siret) {
          setValue("siret", data.siret);
          setIsCompanyFilledUsed(true);
        }
        if (data.label) setValue("name", data.label);
        if (data.address) setValue("adress", data.address);
        if (data.postalCode) setValue("postalCode", data.postalCode);
        if (data.city) setValue("city", data.city);

        fuzzySearchOnCompaniesName(data.label);
      } else {
        setValue("name", normalizeName(data));
        fuzzySearchOnCompaniesName(data);
        trigger("name");
      }
    },
    [setValue, setIsCompanyFilledUsed, fuzzySearchOnCompaniesName, trigger]
  );

  const EMAIL_RULES = {
    pattern: {
      value: emailRegexp,
      message: "Veuillez saisir un e-mail valide",
    },
  };

  const NAME_RULES = {
    pattern: {
      value: nameRegexp,
      message: NAME_ERROR_MSG,
    },
    validate: (v: string) =>
      validateName(v) ||
      (isAgency(companyTypes)
        ? "Une agence transporteur avec ce nom existe déjà pour ce transporteur"
        : "Une entreprise avec ce nom existe déjà"),
  };

  const SIRET_RULES = {
    required: !isOnlySupplier ? "Veuillez remplir ce champ" : false,
    pattern: {
      value: /^([0-9]){14}$/,
      message: "14 chiffres nécessaires",
    },
    validate: (siret) =>
      validateSiret(siret) ||
      (isAgency(companyTypes)
        ? "Une agence transporteur avec ce SIRET existe déjà pour ce transporteur"
        : "Une entreprise avec ce SIRET existe déjà"),
  };

  const showSiretAutofill =
    /^([0-9]){14}$/.test(siretValue) &&
    !isAutofillAlreadyUsed &&
    !isCompanyFilledUsed;

  return (
    <>
      {isAgency(companyTypes) && (
        <div className={classes.formRow}>
          <div className={classes.inputWithAddBtn}>
            <CField
              control={control}
              disabled={
                isAgency(defaultValues?.companyTypes) &&
                !!formDefaultValues.agencyOf
              }
              name="agencyOf"
              required
              error={errors.agencyOf}
              label="Transporteur"
            >
              {(props: CFieldInputProps) => (
                <CompanySelect
                  companyTypes={[CompanyType.CARRIER]}
                  {...props}
                  ignoreSideBarCompanyFilter
                  minCharsToTrigerSearch={0}
                  rowsPerPage={99999}
                />
              )}
            </CField>
          </div>
        </div>
      )}

      <div className={classes.formRow}>
        <div className={classes.formCol}>
          <CField
            control={control}
            label="Nom"
            disabled={!isAdmin()}
            name="name"
            error={errors.name}
            required
            rules={NAME_RULES}
          >
            {(props) => (
              <CompanySiretSelect
                value={props.value}
                onChange={companySiretSelect}
                isAgency={isAgency(companyTypes)}
              />
            )}
          </CField>
        </div>

        <div className={classes.formCol}>
          <CField
            label="SIRET"
            placeholder="Tapez le SIRET"
            disabled={!isAdmin()}
            control={control}
            name="siret"
            error={errors.siret}
            required={!isOnlySupplier}
            rules={SIRET_RULES}
          />
          {showSiretAutofill && (
            <Text
              type="important"
              onClick={handleAutoFillClicked}
              style={{ cursor: "pointer", marginTop: 16 }}
            >
              Préremplir les champs
            </Text>
          )}
          {isAutofillError && (
            <Text type="error" style={{ marginTop: 16 }}>
              Impossible de préremplir les champs
            </Text>
          )}
        </div>

        {isAgency(companyTypes) && (
          <div className={classes.formCol}>
            <CField
              label="E-mail de l'agence transporteur"
              control={control}
              name="email"
              error={errors.email}
              placeholder="Tapez l'e-mail"
              rules={EMAIL_RULES}
            />
          </div>
        )}
      </div>

      {matchingCompanies?.length > 0 && (
        <div className={classes.formRow}>
          <div className={classes.formCol}>
            {matchingCompanies.map((matchingWarehouse) => (
              <Text key={matchingWarehouse._id} type="warning">
                {`Une ou plusieurs entreprise portent un nom similaire : [${matchingWarehouse.name}].`}
              </Text>
            ))}
            <span>
              Nous vous conseillons de consulter la liste des entreprises afin
              de ne pas créer de doublons.
            </span>
          </div>
        </div>
      )}

      {!isCarrier && (
        <CField
          control={control}
          name="sector"
          required
          error={errors.sector}
          label="Secteur"
          placeholder="Sélectionnez un secteur"
        >
          {(props: CFieldInputProps) => <SectorSelect {...props} />}
        </CField>
      )}
    </>
  );
}
